import { RSAA } from 'redux-api-middleware';
import type { RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import type { CustomerSpdPresence, CustomerSpdSaveItem, CustomerSpdValues } from '../types/customerSpd.types';
import type { CustomerCardStoreState } from '../types/CustomerCardTypes';
import { getCustomerCardBffBaseUrl } from '../config';
import { getBuyer, getCoBuyer, getDealId, getDealerId } from './selectors';
import {
    CHECK_CUSTOMER_SPD_REQUEST,
    CHECK_CUSTOMER_SPD_SUCCESS,
    CHECK_CUSTOMER_SPD_FAILURE,
    GET_CUSTOMER_SPD_REQUEST,
    GET_CUSTOMER_SPD_SUCCESS,
    GET_CUSTOMER_SPD_FAILURE,
    SAVE_CUSTOMER_SPD_REQUEST,
    SAVE_CUSTOMER_SPD_SUCCESS,
    SAVE_CUSTOMER_SPD_FAILURE,
    RESET_CUSTOMER_SPD_LOAD_STATE
} from './customerSpd.actions';

type SpdThunkDispatch = (action: any) => any;
// dealId and dealerId both come from this activity's own slice (seeded by the host), so the SPD
// thunks only need the CustomerCardStoreState — no dependency on the host's shared dealer slice.
type SpdGetState = () => CustomerCardStoreState;

/**
 * SPD request headers. The BFF uses `x-dr-dealxgid` / `x-dr-dealerid` for routing/auth
 * Both `dealId` and `dealerId` are deal-context values read from the slice.
 */
const spdHeaders = (dealId: string, dealerId: string): Record<string, string> => ({
    'Content-Type': 'application/json',
    'x-dr-dealxgid': dealId,
    'x-dr-dealerid': dealerId
});

/**
 * CHECK — presence flags. With a `commonConsumerId` it checks that one participant
 * (`/checkcustomerspd/{ccid}`); without it, the BFF returns both buyer + co-buyer
 * (`/checkcustomerspd`). Thunk reads the session constants (bffBaseUrl from config; dealId/dealerId +
 * CCIDs from the store). `meta.commonConsumerIds` lets the reducer resolve loading / 404 / failure.
 */
export const checkCustomerSpdRSAA = (commonConsumerId?: string) => (dispatch: SpdThunkDispatch, getState: SpdGetState) => {
    const state = getState();
    const bffBaseUrl = getCustomerCardBffBaseUrl();
    const dealId = getDealId(state);
    if (!bffBaseUrl || !dealId) return;
    // When a CCID is given, check just that one; otherwise check both participants.
    const commonConsumerIds = commonConsumerId
        ? [commonConsumerId]
        : ([getBuyer(state)?.commonConsumerId, getCoBuyer(state)?.commonConsumerId].filter(Boolean) as string[]);
    const basePath = `${bffBaseUrl}/deal/${dealId}/checkcustomerspd`;
    return dispatch({
        [RSAA]: {
            endpoint: commonConsumerId ? `${basePath}/${commonConsumerId}` : basePath,
            method: 'GET',
            headers: spdHeaders(dealId, String(getDealerId(state))),
            types: [
                { type: CHECK_CUSTOMER_SPD_REQUEST, meta: { commonConsumerIds } },
                { type: CHECK_CUSTOMER_SPD_SUCCESS, meta: { commonConsumerIds } },
                { type: CHECK_CUSTOMER_SPD_FAILURE, meta: { commonConsumerIds } }
            ]
        } satisfies RSAACallRequest
    });
};

/** Response body for GET customerspd: PII values keyed by commonConsumerId. */
type GetCustomerSpdResponse = Record<string, CustomerSpdValues>;

/**
 * GET — actual SPD values for one participant, fetched on demand (Show Data click).
 *
 * The response (PII) is delivered to `onData` from inside redux-api-middleware's `payload` fn — it
 * runs *before* the SUCCESS FSA is passed downstream, so it is unaffected by dash middleware that
 * call `next()` without returning it (which makes the dispatch promise resolve to `undefined`; see
 * offerMiddleware). PII never enters Redux — the reducer has NO GET success case, and `payload`
 * hands the parsed body straight to the component. `meta.commonConsumerId` attributes a 401.
 *
 * `onError` receives the HTTP status so the component can distinguish 401 (unauthorized — dealCentralAuthorizer
 * denied access) from other failures and show the appropriate message.
 */
export const getCustomerSpdRSAA =
    (commonConsumerId: string, onData: (values: CustomerSpdValues) => void, onError: (status?: number) => void) =>
    (dispatch: SpdThunkDispatch, getState: SpdGetState) => {
        const state = getState();
        const bffBaseUrl = getCustomerCardBffBaseUrl();
        const dealId = getDealId(state);
        if (!bffBaseUrl || !dealId) {
            onError();
            return Promise.resolve(undefined);
        }
        return dispatch({
            [RSAA]: {
                endpoint: `${bffBaseUrl}/deal/${dealId}/customerspd/${commonConsumerId}`,
                method: 'GET',
                headers: spdHeaders(dealId, String(getDealerId(state))),
                types: [
                    {
                        type: GET_CUSTOMER_SPD_REQUEST,
                        meta: { commonConsumerId }
                    },
                    {
                        type: GET_CUSTOMER_SPD_SUCCESS,
                        meta: { commonConsumerId },
                        // Runs inside the api middleware; parse the body and hand PII to the component.
                        payload: async (_action: unknown, _stateArg: unknown, res: Response) => {
                            const body = (await res.json().catch(() => ({}))) as GetCustomerSpdResponse;
                            onData(body?.[commonConsumerId] ?? {});
                            return body;
                        }
                    },
                    {
                        type: GET_CUSTOMER_SPD_FAILURE,
                        meta: { commonConsumerId },
                        // Failure also runs inside the api middleware; signal the component to collapse.
                        payload: (_action: unknown, _stateArg: unknown, res?: Response) => {
                            onError(res?.status);
                            return { status: res?.status };
                        }
                    }
                ]
            } satisfies RSAACallRequest
        });
    };

/**
 * SAVE — persist changed SPD for all changed participants in one array POST, on apply.
 * The thunk dispatches RSAA actions; the reducer records presence on success and saveError on failure.
 * The hook reads the error state after dispatch to determine whether to fail the apply operation.
 */
export const saveCustomerSpdRSAA = (items: CustomerSpdSaveItem[]) => (dispatch: SpdThunkDispatch, getState: SpdGetState) => {
    const state = getState();
    const bffBaseUrl = getCustomerCardBffBaseUrl();
    const dealId = getDealId(state);
    if (!bffBaseUrl || !dealId) return Promise.resolve(undefined);
    return dispatch({
        [RSAA]: {
            endpoint: `${bffBaseUrl}/saveCustomerSpd/${dealId}`,
            method: 'POST',
            headers: spdHeaders(dealId, String(getDealerId(state))),
            body: JSON.stringify(items),
            types: [SAVE_CUSTOMER_SPD_REQUEST, { type: SAVE_CUSTOMER_SPD_SUCCESS, meta: { items } }, SAVE_CUSTOMER_SPD_FAILURE]
        } satisfies RSAACallRequest
    });
};

/**
 * Dispatched when the customer card slideout opens to reset the SPD slice back to its initial
 * state (clears checkLoading, presence, errors, etc.) so each open starts fresh.
 */
export const resetCustomerSpdLoadState = () => ({ type: RESET_CUSTOMER_SPD_LOAD_STATE as typeof RESET_CUSTOMER_SPD_LOAD_STATE });

/**
 * SPD RSAA lifecycle actions dispatched by redux-api-middleware, consumed by the SPD reducer.
 */
export type CustomerSpdAction =
    | { type: typeof CHECK_CUSTOMER_SPD_REQUEST; meta?: { commonConsumerIds?: string[] } }
    | {
          type: typeof CHECK_CUSTOMER_SPD_SUCCESS;
          // canDesk is a flat sibling of the CCID keys, not per-participant data.
          payload?: { ok?: boolean } | ({ canDesk?: boolean } & { [commonConsumerId: string]: CustomerSpdPresence }) | null;
          meta?: { commonConsumerIds?: string[] };
      }
    | { type: typeof CHECK_CUSTOMER_SPD_FAILURE; payload?: { status?: number }; meta?: { commonConsumerIds?: string[] } }
    | { type: typeof GET_CUSTOMER_SPD_REQUEST; meta?: { commonConsumerId?: string } }
    | { type: typeof GET_CUSTOMER_SPD_FAILURE; payload?: { status?: number }; meta?: { commonConsumerId?: string } }
    | { type: typeof GET_CUSTOMER_SPD_SUCCESS; payload?: unknown; meta?: { commonConsumerId?: string } }
    | { type: typeof SAVE_CUSTOMER_SPD_REQUEST }
    | {
          type: typeof SAVE_CUSTOMER_SPD_SUCCESS;
          // payload = response body: the participants the BFF confirmed saved (array of { ccid }).
          payload?: Array<{ ccid: string }> | null;
          meta?: { items?: CustomerSpdSaveItem[] };
      }
    | { type: typeof SAVE_CUSTOMER_SPD_FAILURE; payload?: unknown }
    | { type: typeof RESET_CUSTOMER_SPD_LOAD_STATE };
